import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Receipt, Trash2, Plus, FileSpreadsheet, FileText, ChevronLeft, ChevronRight, ChevronDown,
  CalendarDays, CalendarRange, Calendar,
} from 'lucide-react';
import {
  getDay, getDates, getMonth, getWeek, deletePayment, urlExportIngresos, DayResp, MonthResp, WeekResp, DesgloseMensualidades, DesgloseFila, Payment, PlanChip,
  moneyEntero as money, TYPE_LABEL, METHOD_LABEL,
} from '../api';
import PaymentForm from './PaymentForm';
import EmptyState from '../../../components/EmptyState';
import CountUp from '../../../components/CountUp';
import { Skeleton } from '../../../components/Skeleton';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import Avatar from '../../../components/Avatar';
import PerfilLink, { rutaPerfil } from './PerfilLink';
import MetodoPago from './MetodoPago';
import { useRefrescoAlVolver } from '../../../hooks/useRefrescoAlVolver';
import { todayLocal, fromISO, toISO } from '../../../utils/dates';

/**
 * INGRESOS — la pestaña «Payments» de Kydemy, que Karla pidió que se llamara Ingresos
 * (audio de Carlos 18-sep-2026). F2 del rediseño de caja.
 *
 * Lo que la hace distinta de la lista plana que teníamos:
 *  · DÍA  → bloques por concepto y, dentro de Mensualidades, **subtotal por PLAN** (1D, B3, FEM…),
 *           que es como ella lee el día. Cada renglón con la cara del alumno y del cajero, el
 *           **icono del método** (billete / banco / tarjeta) y el chip de color de su membresía.
 *  · SEMANA → (Carlos 22-sep-2026) las mismas dos tablas para la semana lunes→domingo, más el
 *           desglose por día, que es lo que la hace útil frente al mes.
 *  · MES  → las dos tablas de resumen de Kydemy: por concepto y **por método de pago**, que es el
 *           «ahí aparece efectivo y también transferencias o tarjetas» del audio.
 *
 * Día inicial y flechas: al entrar, si hoy aún no tiene cobros, se abre en el último día que sí
 * los tiene (como Kydemy). Las flechas van día por día, también por los días sin cobros
 * (23-sep-2026: antes saltaban solo entre días con pagos y el 22, vacío, era inalcanzable).
 */

/** Chip del plan con su color de catálogo. Cae a gris si el plan ya no existe. */
function ChipPlan({ plan }: { plan?: PlanChip }) {
  if (!plan?.code) return null;
  const c = plan.color && /^#[0-9a-f]{6}$/i.test(plan.color) ? plan.color : null;
  return (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-white"
      style={c ? { backgroundColor: c } : undefined}
      title={plan.name}>
      {plan.code}
    </span>
  );
}

function fmtTime(s: string) { return String(s).slice(11, 16); }
function fmtDate(d: string) {
  const x = new Date(d + 'T12:00:00');
  return isNaN(x.getTime()) ? d : x.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}
/** '2026-09' / '2026-09-01' → «sep 26». Kydemy pone solo el mes; el año evita ambigüedad
 *  cuando se cobra por adelantado en diciembre. Si no parsea, se muestra tal cual. */
function mesCorto(v: string | null) {
  if (!v) return '';
  const d = new Date(String(v).slice(0, 7) + '-01T12:00:00');
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' }).replace('.', '');
}
function mesBonito(ym: string) {
  const d = new Date(ym + '-01T12:00:00');
  if (isNaN(d.getTime())) return ym;
  const s = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** '2026-09-21' → «lun 21». */
function diaCorto(iso: string) {
  return fromISO(iso).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' }).replace('.', '');
}
/** «21 sep – 27 sep 2026» (o «28 sep – 4 oct 2026» si cruza de mes). */
function rangoBonito(from: string, to: string) {
  const f = (iso: string, conAnio: boolean) =>
    fromISO(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', ...(conAnio ? { year: 'numeric' } : {}) }).replace('.', '');
  return `${f(from, false)} – ${f(to, true)}`;
}
/** Los 7 días ISO de la semana que arranca en `lunes`. */
function diasDeSemana(lunes: string) {
  return Array.from({ length: 7 }, (_, i) => { const d = fromISO(lunes); d.setDate(d.getDate() + i); return toISO(d); });
}

/** Orden de los bloques del día: como en Kydemy, primero lo que más pesa. */
const ORDEN = ['membership', 'voucher_trial', 'license', 'class', 'voucher', 'event', 'product', 'other', 'refund'];

export default function IngresosPanel({ abrirFormInicial = false, recarga = 0 }: { abrirFormInicial?: boolean; recarga?: number } = {}) {
  const { user, hasModule } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  // Cajera sin Finanzas (Carlos 29-sep-2026): «solo pueden ver lo del día» → sin Semana/Mes, sin
  // cambiar de fecha y sin Excel/PDF (el servidor además rechaza otro día).
  const soloHoy = !hasModule('letsmove_finances');

  const [vista, setVista] = useState<'dia' | 'semana' | 'mes'>('dia');
  const [date, setDate] = useState<string>(() => todayLocal());
  // Cualquier día de la semana que se mira; el backend calcula lunes y domingo.
  const [semanaDe, setSemanaDe] = useState<string>(() => todayLocal());
  const [week, setWeek] = useState<WeekResp | null>(null);
  const arranque = useRef(true);
  const [, setDates] = useState<{ pay_date: string; total: string; n: number }[]>([]);
  const [day, setDay] = useState<DayResp | null>(null);
  const [mes, setMes] = useState<string>(() => date.slice(0, 7));
  const [month, setMonth] = useState<MonthResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(abrirFormInicial);

  const cargaDia = useCallback(() => {
    setLoading(true);
    getDay(date).then(setDay).catch(() => setDay(null)).finally(() => setLoading(false));
  }, [date]);
  useEffect(() => { if (vista === 'dia') cargaDia(); }, [vista, cargaDia]);
  useEffect(() => {
    getDates().then((r) => {
      setDates(r.data);
      // Al abrir: si hoy no tiene cobros todavía, arrancar en el último día que sí (como Kydemy).
      if (arranque.current) {
        arranque.current = false;
        if (soloHoy) return;
        const hoy = todayLocal();
        const conPagos = r.data.map((d) => d.pay_date.slice(0, 10));
        if (!conPagos.includes(hoy)) {
          const ultimo = conPagos.find((d) => d < hoy);   // vienen DESC → el primero menor es el más cercano
          if (ultimo) setDate(ultimo);
        }
      }
    }).catch(() => setDates([]));
  }, []);
  useEffect(() => {
    if (vista !== 'semana') return;
    setLoading(true);
    getWeek(semanaDe).then(setWeek).catch(() => setWeek(null)).finally(() => setLoading(false));
  }, [vista, semanaDe]);
  useEffect(() => {
    if (vista !== 'mes') return;
    setLoading(true);
    getMonth(mes).then(setMonth).catch(() => setMonth(null)).finally(() => setLoading(false));
  }, [vista, mes]);

  // Flechas día por día, INCLUIDOS los días sin cobros (Carlos 23-sep-2026: «hoy es 23 y no puedo
  // llegar al 22» — saltar solo entre días con pagos escondía los vacíos). Tope: hoy.
  const diaMas = (n: number) => { const d = fromISO(date); d.setDate(d.getDate() + n); setDate(toISO(d)); };
  const semanaMas = (n: number) => { const d = fromISO(semanaDe); d.setDate(d.getDate() + 7 * n); setSemanaDe(toISO(d)); };

  /** name → plan, prefiriendo el vigente cuando hay nombres repetidos con códigos distintos. */
  const planPorNombre = useMemo(() => {
    const m = new Map<string, PlanChip>();
    for (const p of day?.plans || []) if (!m.has(p.name)) m.set(p.name, p);   // vienen ya ordenados
    return m;
  }, [day]);

  /** Bloques por concepto y, dentro de Mensualidades, subtotal por plan. */
  const bloques = useMemo(() => {
    const rows = day?.data || [];
    const porTipo = new Map<string, Payment[]>();
    for (const p of rows) {
      if (!porTipo.has(p.type)) porTipo.set(p.type, []);
      porTipo.get(p.type)!.push(p);
    }
    return [...porTipo.entries()]
      .sort((a, b) => {
        const ia = ORDEN.indexOf(a[0]), ib = ORDEN.indexOf(b[0]);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      })
      .map(([type, filas]) => {
        const subtotal = filas.reduce((s, p) => s + Number(p.amount || 0), 0);
        // Sub-subtotales por plan: solo donde significan algo (mensualidades).
        let porPlan: { nombre: string; plan?: PlanChip; filas: Payment[]; total: number }[] = [];
        if (type === 'membership') {
          const g = new Map<string, Payment[]>();
          for (const p of filas) {
            const k = p.assoc_name || '—';
            if (!g.has(k)) g.set(k, []);
            g.get(k)!.push(p);
          }
          porPlan = [...g.entries()]
            .map(([nombre, fs]) => ({
              nombre, plan: planPorNombre.get(nombre), filas: fs,
              total: fs.reduce((s, p) => s + Number(p.amount || 0), 0),
            }))
            .sort((a, b) => b.total - a.total);
        }
        return { type, filas, subtotal, porPlan };
      });
  }, [day, planPorNombre]);

  // Igual que en Efectivo: al volver a la pantalla se repesca el día (o el mes) que se esté viendo.
  useRefrescoAlVolver(() => {
    if (vista === 'dia') cargaDia();
    else if (vista === 'semana') getWeek(semanaDe).then(setWeek).catch(() => {});
    else getMonth(mes).then(setMonth).catch(() => {});
  }, 120000);

  // Tras pulsar «Actualizar» en la cabecera: volver a pedir el día (y la lista de fechas, que
  // puede haber ganado el día de hoy si era el primer cobro).
  useEffect(() => {
    if (recarga === 0) return;
    if (vista === 'dia') cargaDia();
    else if (vista === 'semana') getWeek(semanaDe).then(setWeek).catch(() => {});
    else getMonth(mes).then(setMonth).catch(() => {});
    getDates().then((r) => setDates(r.data)).catch(() => {});
  }, [recarga]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function onDelete(id: number) {
    if (!confirm('¿Deshacer este pago? El importe deja de contar y, si venía de un adeudo, se reabre.')) return;
    try { await deletePayment(id); toast.success('Pago deshecho'); cargaDia(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo deshacer'); }
  }

  // Excel y PDF los genera el SERVIDOR (22-sep-2026, Carlos: «los CSV vienen feos… un PDF con el
  // logo»): misma consulta que la pantalla, membrete con logo, bloques por concepto y subtotal por
  // plan. Antes: CSV con `;` armado aquí y window.print() de la vista. Rutas: /letsmove/finances/*.xlsx|pdf.
  const valorExport = vista === 'dia' ? date : vista === 'semana' ? semanaDe : mes;
  const BotonesExport = () => (
    <>
      <a href={urlExportIngresos(vista, valorExport, 'xlsx')} download title="Descargar Excel" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
        <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
      </a>
      <a href={urlExportIngresos(vista, valorExport, 'pdf')} target="_blank" rel="noopener" title="Abrir PDF con el logo" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
        <FileText size={16} /> <span className="hidden sm:inline">PDF</span>
      </a>
    </>
  );

  const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40';

  return (
    <div>
      {/* Barra: DÍA/MES + navegación + acciones */}
      <div className="no-print mb-4 flex flex-wrap items-center gap-2">
        {!soloHoy && <div className="flex rounded-lg border border-line bg-surface p-0.5">
          <button onClick={() => setVista('dia')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${vista === 'dia' ? 'bg-accent text-on-accent' : 'text-content-2'}`}>
            <CalendarDays size={15} /> Día
          </button>
          <button onClick={() => setVista('semana')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${vista === 'semana' ? 'bg-accent text-on-accent' : 'text-content-2'}`}>
            <Calendar size={15} /> Semana
          </button>
          <button onClick={() => setVista('mes')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${vista === 'mes' ? 'bg-accent text-on-accent' : 'text-content-2'}`}>
            <CalendarRange size={15} /> Mes
          </button>
        </div>}

        {isAdmin && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
            <Plus size={16} /> Registrar pago
          </button>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {soloHoy ? (
            <span className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2" data-testid="ingresos-solo-hoy">
              <CalendarDays size={15} /> Hoy
            </span>
          ) : vista === 'dia' ? (
            <>
              {!!day?.data.length && <BotonesExport />}
              <button onClick={() => diaMas(-1)} title="Día anterior" className={navBtn}><ChevronLeft size={16} /></button>
              <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
                className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue" />
              <button onClick={() => diaMas(1)} disabled={date >= todayLocal()} title="Día siguiente" className={navBtn}><ChevronRight size={16} /></button>
            </>
          ) : vista === 'semana' ? (
            <>
              {!!week?.count && <BotonesExport />}
              <button onClick={() => semanaMas(-1)} title="Semana anterior" className={navBtn}><ChevronLeft size={16} /></button>
              <input type="date" value={semanaDe} onChange={(e) => e.target.value && setSemanaDe(e.target.value)}
                title="Cualquier día de la semana que quieras ver"
                className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue" />
              <button onClick={() => semanaMas(1)} disabled={semanaDe >= todayLocal()} title="Semana siguiente" className={navBtn}><ChevronRight size={16} /></button>
            </>
          ) : (
            <>
              {!!month?.count && <BotonesExport />}
              <input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
                className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue" />
            </>
          )}
        </div>
      </div>

      {showForm && <PaymentForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); cargaDia(); }} />}

      <div className="print-area">
        {loading ? (
          <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : vista === 'semana' ? (
          /* ── VISTA SEMANA: total + comparativo, desglose por día y las dos tablas ── */
          !week ? (
            <div className="rounded-2xl border border-line bg-surface">
              <EmptyState icon={Receipt} title="Sin datos" subtitle="No se pudo cargar la semana." />
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-line bg-surface p-5 shadow-sm">
                <div>
                  <div className="text-sm text-content-2">Semana del {rangoBonito(week.from, week.to)}</div>
                  <div className="font-display text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                    <CountUp value={week.total} format={money} />
                  </div>
                  <div className="text-xs text-content-3">{week.count} cobros</div>
                </div>
                {week.prevTotal > 0 && (() => {
                  const d = week.total - week.prevTotal;
                  const pct = (100 * d / week.prevTotal);
                  return (
                    <div className="text-right text-sm">
                      <div className="text-content-3">vs. semana del {rangoBonito(week.prevFrom, week.prevTo)}</div>
                      <div className={d >= 0 ? 'font-semibold text-emerald-600 dark:text-emerald-400' : 'font-semibold text-red-600 dark:text-red-400'}>
                        {d >= 0 ? '+' : '−'}{money(Math.abs(d))} ({pct >= 0 ? '+' : '−'}{Math.abs(pct).toFixed(1)}%)
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Los 7 días, con los vacíos en gris; tocar uno abre la vista Día. */}
              <div className="mb-4 grid grid-cols-4 gap-2 sm:grid-cols-7">
                {diasDeSemana(week.from).map((d) => {
                  const r = week.byDay.find((x) => x.day === d);
                  const total = Number(r?.total || 0);
                  const esHoy = d === todayLocal();
                  return (
                    <button key={d} type="button" onClick={() => { setDate(d); setVista('dia'); }}
                      disabled={!r}
                      className={`rounded-xl border px-2 py-2 text-left transition ${r ? 'border-line bg-surface hover:bg-surface-2' : 'border-line/50 bg-surface/50 opacity-60'} ${esHoy ? 'ring-1 ring-brand-blue' : ''}`}
                      title={r ? `${r.n} cobros · abrir el día` : 'Sin cobros'}>
                      <div className="text-[11px] uppercase tracking-wide text-content-3">{diaCorto(d)}</div>
                      <div className={`text-sm font-semibold tabular-nums ${r ? 'text-content' : 'text-content-3'}`}>{money(total)}</div>
                      <div className="text-[11px] text-content-3">{r ? `${r.n} cobros` : '—'}</div>
                    </button>
                  );
                })}
              </div>

              {week.count === 0 ? (
                <div className="rounded-2xl border border-line bg-surface">
                  <EmptyState icon={Receipt} title="Sin cobros esta semana" subtitle="No se registró ningún pago entre lunes y domingo." />
                </div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  <Tabla titulo="Por concepto" filas={week.byType.map((r) => ({ k: TYPE_LABEL[r.type] || r.type, n: r.n, total: Number(r.total) }))} total={week.total}
                    desgloses={desglosesDe(week.mensualidades, week.otros, week.clasesPrueba)} />
                  <Tabla titulo="Por método de pago" icono
                    filas={week.byMethod.map((r) => ({ k: METHOD_LABEL[r.method] || r.method, n: r.n, total: Number(r.total), metodo: r.method }))}
                    total={week.total} />
                </div>
              )}
            </>
          )
        ) : vista === 'mes' ? (
          /* ── VISTA MES: las dos tablas de resumen de Kydemy ── */
          !month ? (
            <div className="rounded-2xl border border-line bg-surface">
              <EmptyState icon={Receipt} title="Sin datos" subtitle="No hay cobros en el mes seleccionado." />
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-line bg-surface p-5 shadow-sm">
                <div>
                  <div className="text-sm capitalize text-content-2">{mesBonito(month.month)}</div>
                  <div className="font-display text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                    <CountUp value={month.total} format={money} />
                  </div>
                  <div className="text-xs text-content-3">{month.count} cobros</div>
                </div>
                {month.prevTotal > 0 && (() => {
                  const d = month.total - month.prevTotal;
                  const pct = (100 * d / month.prevTotal);
                  return (
                    <div className="text-right text-sm">
                      <div className="text-content-3">vs. {mesBonito(month.prevMonth)}</div>
                      <div className={d >= 0 ? 'font-semibold text-emerald-600 dark:text-emerald-400' : 'font-semibold text-red-600 dark:text-red-400'}>
                        {d >= 0 ? '+' : '−'}{money(Math.abs(d))} ({pct >= 0 ? '+' : '−'}{Math.abs(pct).toFixed(1)}%)
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Tabla titulo="Por concepto" filas={month.byType.map((r) => ({ k: TYPE_LABEL[r.type] || r.type, n: r.n, total: Number(r.total) }))} total={month.total}
                  desgloses={desglosesDe(month.mensualidades, month.otros, month.clasesPrueba)} />
                <Tabla titulo="Por método de pago" icono
                  filas={month.byMethod.map((r) => ({ k: METHOD_LABEL[r.method] || r.method, n: r.n, total: Number(r.total), metodo: r.method }))}
                  total={month.total} />
              </div>
            </>
          )
        ) : (
          /* ── VISTA DÍA: bloques por concepto, con subtotal por plan en mensualidades ── */
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div>
                <div className="text-sm capitalize text-content-2">{fmtDate(date)}</div>
                <div className="font-display text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  <CountUp value={day?.totals.grand || 0} format={money} />
                </div>
                <div className="text-xs text-content-3">{day?.totals.count || 0} cobros</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(day?.totals.byMethod || {}).map(([m, v]) => (
                  <span key={m} className="flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-sm text-content-2">
                    <MetodoPago metodo={m} conTexto /> <b className="text-content">{money(v)}</b>
                  </span>
                ))}
              </div>
            </div>

            {!day?.data.length ? (
              <div className="rounded-2xl border border-line bg-surface">
                <EmptyState icon={Receipt} title="Sin cobros este día" subtitle="No se registró ningún pago en la fecha seleccionada." />
              </div>
            ) : bloques.map((b) => (
              <div key={b.type} className="mb-4 overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
                <div className="flex items-center justify-between border-b border-line bg-surface-2 px-4 py-2">
                  <span className="font-semibold text-content">{TYPE_LABEL[b.type] || b.type}</span>
                  <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                    {money(b.subtotal)} <span className="font-normal text-content-3">({b.filas.length})</span>
                  </span>
                </div>

                {b.porPlan.length ? (
                  // Mensualidades: un sub-bloque por plan, con su «Sub Total» como en Kydemy
                  b.porPlan.map((g) => (
                    <div key={g.nombre}>
                      <Filas filas={g.filas} planPorNombre={planPorNombre} isAdmin={isAdmin} onDelete={onDelete} />
                      {b.porPlan.length > 1 && (
                        <div className="flex items-center justify-end gap-2 border-t border-line/60 bg-surface-2/20 px-4 py-1 text-xs">
                          <span className="text-content-3">Subtotal</span>
                          <ChipPlan plan={g.plan} />
                          <span className="text-content-3">{g.nombre}</span>
                          <span className="ml-1 w-24 text-right font-semibold tabular-nums text-content-2">{money(g.total)}</span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <Filas filas={b.filas} planPorNombre={planPorNombre} isAdmin={isAdmin} onDelete={onDelete} />
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/** Subfilas de «Mensualidad» (22-sep-2026, Carlos): los planes con su chip, y una nota con los
 *  cobros trimestrales / a medida SOLO si los hubo — si todo es mensual, la fila queda limpia. */
function desgloseMensualidad(m?: DesgloseMensualidades): Desglose | undefined {
  if (!m || !m.byPlan.length) return undefined;
  const raros = m.byPeriodo.filter((p) => p.periodo !== 'mensual');
  const nota = raros.length
    ? 'de ellas: ' + raros.map((p) => `${p.n} ${p.periodo === 'trimestral' ? (p.n === 1 ? 'trimestral' : 'trimestrales') : 'a medida'} · ${money(Number(p.total))}`).join(' · ')
    : undefined;
  return { key: TYPE_LABEL.membership, filas: m.byPlan.map((p) => ({ k: p.name, n: p.n, total: Number(p.total), code: p.code, color: p.color })), nota };
}
interface Desglose { key: string; filas: { k: string; n: number; total: number; code?: string | null; color?: string | null }[]; nota?: string }
/** Las filas expandibles de «Por concepto» (Carlos 22-sep, «con eso cerramos»): Mensualidad → planes;
 *  Otro pago → agua y botanas / multas / merch; Clase de prueba → por clase. Inscripción no tiene subtipo. */
function desglosesDe(m?: DesgloseMensualidades, otros?: DesgloseFila[], pruebas?: DesgloseFila[]): Desglose[] {
  const out: Desglose[] = [];
  const dm = desgloseMensualidad(m); if (dm) out.push(dm);
  if (otros && otros.length > 1) out.push({ key: TYPE_LABEL.other, filas: otros.map((f) => ({ k: f.label, n: f.n, total: Number(f.total) })) });
  if (pruebas && pruebas.length) out.push({ key: TYPE_LABEL.voucher_trial, filas: pruebas.map((f) => ({ k: f.label, n: f.n, total: Number(f.total) })) });
  return out;
}

/** Tabla de resumen del mes (concepto o método): nº de cobros + importe + % del total.
 *  `desglose`: la fila cuyo `k` coincida se puede expandir (Mensualidad → planes). */
function Tabla({ titulo, filas, total, icono, desgloses }: {
  titulo: string; total: number; icono?: boolean;
  filas: { k: string; n: number; total: number; metodo?: string }[];
  desgloses?: Desglose[];
}) {
  const [abiertos, setAbiertos] = useState<Set<string>>(() => new Set());
  const alternar = (k: string) => setAbiertos((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
      <div className="border-b border-line bg-surface-2 px-4 py-2 font-semibold text-content">{titulo}</div>
      <table className="w-full text-sm">
        <thead className="text-content-3">
          <tr className="text-[11px] uppercase tracking-wide">
            <th className="px-4 py-1.5 text-left font-medium">Concepto</th>
            <th className="w-20 px-2 py-1.5 text-right font-medium">Cobros</th>
            <th className="w-28 px-4 py-1.5 text-right font-medium">Importe</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const desglose = desgloses?.find((d) => d.key === f.k);
            const expandible = !!desglose;
            const abierto = abiertos.has(f.k);
            return (
              <Fragment key={f.k}>
                <tr className={`border-t border-line/60 ${expandible ? 'cursor-pointer select-none hover:bg-surface-2/60' : ''}`}
                  onClick={expandible ? () => alternar(f.k) : undefined}
                  title={expandible ? (abierto ? 'Ocultar desglose' : 'Ver desglose') : undefined}>
                  <td className="px-4 py-1.5 text-content">
                    <span className="flex items-center gap-1.5">
                      {expandible && <ChevronDown size={14} className={`text-content-3 transition ${abierto ? 'rotate-180' : ''}`} />}
                      {icono && f.metodo ? <MetodoPago metodo={f.metodo} conTexto /> : f.k}
                      {total > 0 && <span className="text-xs text-content-3">{(100 * f.total / total).toFixed(0)}%</span>}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-content-3">{f.n}</td>
                  <td className="px-4 py-1.5 text-right font-medium tabular-nums text-content">{money(f.total)}</td>
                </tr>
                {expandible && abierto && desglose!.filas.map((p) => (
                  <tr key={p.k} className="bg-surface-2/40 text-xs">
                    <td className="py-1 pl-10 pr-4 text-content-2">
                      <span className="flex items-center gap-1.5">
                        {p.code && <ChipPlan plan={{ name: p.k, code: p.code, color: p.color || null } as PlanChip} />}
                        {p.k}
                        {f.total > 0 && <span className="text-content-3">{(100 * p.total / f.total).toFixed(0)}%</span>}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-content-3">{p.n}</td>
                    <td className="px-4 py-1 text-right tabular-nums text-content-2">{money(p.total)}</td>
                  </tr>
                ))}
                {expandible && abierto && desglose!.nota && (
                  <tr className="bg-surface-2/40 text-xs"><td colSpan={3} className="py-1 pl-10 pr-4 italic text-content-3">{desglose!.nota}</td></tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-line bg-surface-2/50">
            <td className="px-4 py-1.5 text-xs uppercase tracking-wide text-content-3">Total</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-content-2">{filas.reduce((s, f) => s + f.n, 0)}</td>
            <td className="px-4 py-1.5 text-right font-bold tabular-nums text-content">{money(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * El texto de detalle de un cobro, ya legible.
 *
 * 🪤 18-sep-2026 — Kydemy mete en `assoc_name` cosas MUY distintas según el tipo, y varias con su
 * formato interno a la vista. Lo estábamos pintando crudo:
 *   · `voucher_trial` → `{"Ballet 3"}`   ← llaves y comillas de un array de Postgres
 *   · `license`       → `2026-12-31`     ← es la fecha de VENCIMIENTO, sin decirlo
 *   · `other`         → `ayb`            ← y el qué se vendió está en `comment`, que guardamos
 *                                           en 2.535 cobros y no enseñaba nadie
 * Kydemy limpia esto en su pantalla (pinta «K-POP ADULTOS · Sep 18th» y «Sun 13, Dec 2026»).
 * La fecha de la clase de prueba vive dentro de `assoc_code`, que es un JSON.
 */
function detalleCobro(p: Payment): string {
  const limpio = (v?: string | null) => (v || '').replace(/^\{"?|"?\}$/g, '').replace(/^"|"$/g, '').trim();
  const fechaLarga = (iso: string) => {
    const d = new Date(iso + 'T12:00:00');
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  if (p.type === 'voucher_trial' || p.type === 'voucher_voucher') {
    const clase = limpio(p.assoc_name);
    let cuando = '';
    try {
      const j = JSON.parse(p.assoc_code || '{}');
      if (Array.isArray(j.class_dates) && j.class_dates[0]) cuando = ` · ${fechaLarga(j.class_dates[0])}`;
    } catch { /* assoc_code no siempre es JSON; el nombre de la clase ya basta */ }
    return (clase || 'Clase de prueba') + cuando;
  }
  if (p.type === 'license') {
    const f = (p.assoc_name || '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(f) ? `vence ${fechaLarga(f)}` : f;
  }
  // El comentario es lo que de verdad se vendió («Aracely coca y mazapán»); el assoc_name suele
  // ser solo la categoría («ayb»). Se enseñan los dos, sin repetir si coinciden.
  const base = limpio(p.assoc_name);
  const com = (p.comment || '').trim();
  if (com && com.toLowerCase() !== base.toLowerCase()) return base ? `${base} · ${com}` : com;
  return base;
}

/** Renglones de cobro: cara del alumno, icono de método, plan y cara del cajero. */
function Filas({ filas, planPorNombre, isAdmin, onDelete }: {
  filas: Payment[]; planPorNombre: Map<string, PlanChip>;
  isAdmin: boolean; onDelete: (id: number) => void;
}) {
  return (
    <>
      {/* Escritorio */}
      <table className="hidden w-full text-sm sm:table">
        <tbody>
          {filas.map((p) => (
            <tr key={p.payment_id} className="group border-t border-line/60 transition hover:bg-surface-2/60">
              <td className="w-16 px-4 py-2 tabular-nums text-xs text-content-3">{fmtTime(p.datetime)}</td>
              <td className="w-8 py-2 sm:w-28"><MetodoPago metodo={p.method} conTexto="sm" /></td>
              <td className="px-2 py-2">
                <span className="flex items-center gap-2">
                  {/* Clic → perfil de la alumna o del maestro (Carlos 29-sep). */}
                  <PerfilLink to={rutaPerfil({ contactId: p.contact_id, userId: p.payer_user_id, esProfe: p.payer_es_profe })}
                    title={p.payer || undefined} className="flex min-w-0 items-center gap-2 text-content">
                    <Avatar kind="contact" id={p.contact_id} name={p.payer} size={24} />
                    <span className="truncate">{p.payer || '—'}</span>
                  </PerfilLink>
                  {p.kydemy_id == null && <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent-ink">manual</span>}
                </span>
              </td>
              <td className="px-2 py-2">
                <span className="flex items-center gap-1.5">
                  <ChipPlan plan={planPorNombre.get(p.assoc_name || '')} />
                  {/* Sin código de plan (eventos, bonos, otros) se enseña el texto; si hay chip,
                      el nombre largo sobra: va en su `title`. */}
                  {!planPorNombre.get(p.assoc_name || '')?.code && (
                    <span className="truncate text-xs text-content-2" title={detalleCobro(p)}>{detalleCobro(p)}</span>
                  )}
                </span>
              </td>
              <td className="w-20 px-2 py-2 whitespace-nowrap text-xs text-content-3">{mesCorto(p.months)}</td>
              <td className="w-28 px-2 py-2">
                {p.teller && (
                  <PerfilLink to={rutaPerfil({ userId: p.teller_id, esProfe: p.teller_es_profe })} title={p.teller}
                    className="flex items-center gap-1.5">
                    <Avatar kind="user" id={p.teller_id} name={p.teller} size={20} />
                    <span className="hidden truncate text-xs text-content-3 lg:inline">{p.teller.split(' ')[0]}</span>
                  </PerfilLink>
                )}
              </td>
              <td className="w-24 px-2 py-2 text-right font-medium tabular-nums text-content">{money(p.amount)}</td>
              <td className="w-16 px-3 py-2 text-right">
                <span className="no-print flex items-center justify-end gap-1.5">
                  <Link to={`/recibo/${p.payment_id}`} title="Ver / imprimir recibo" className="text-content-3 transition hover:text-brand-blue">
                    <Receipt size={15} />
                  </Link>
                  {isAdmin && p.kydemy_id == null && (
                    <button onClick={() => onDelete(p.payment_id)} title="Deshacer pago"
                      className="text-content-3 opacity-0 transition hover:text-red-500 group-hover:opacity-100">
                      <Trash2 size={15} />
                    </button>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Móvil */}
      <div className="divide-y divide-line sm:hidden">
        {filas.map((p) => (
          <div key={p.payment_id} className="flex items-start justify-between gap-3 px-4 py-2.5">
            <div className="flex min-w-0 items-start gap-2">
              <Avatar kind="contact" id={p.contact_id} name={p.payer} size={28} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-1.5">
                  <PerfilLink to={rutaPerfil({ contactId: p.contact_id, userId: p.payer_user_id, esProfe: p.payer_es_profe })}
                    className="truncate font-medium text-content">{p.payer || '—'}</PerfilLink>
                  <ChipPlan plan={planPorNombre.get(p.assoc_name || '')} />
                </div>
                {/* En móvil/tablet —la pantalla del mostrador— el detalle también se ve: es
                    donde está lo vendido y la fecha de la clase de prueba. */}
                {!planPorNombre.get(p.assoc_name || '')?.code && detalleCobro(p) && (
                  <div className="truncate text-xs text-content-2">{detalleCobro(p)}</div>
                )}
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-content-3">
                  <MetodoPago metodo={p.method} conTexto size={13} />
                  {fmtTime(p.datetime)}
                  {p.months ? ` · ${mesCorto(p.months)}` : ''}
                  {p.teller ? ` · ${p.teller.split(' ')[0]}` : ''}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className="font-semibold tabular-nums text-content">{money(p.amount)}</span>
              <span className="no-print flex items-center gap-2.5">
                <Link to={`/recibo/${p.payment_id}`} title="Recibo" className="text-content-3 transition hover:text-brand-blue">
                  <Receipt size={16} />
                </Link>
                {isAdmin && p.kydemy_id == null && (
                  <button onClick={() => onDelete(p.payment_id)} title="Deshacer pago" className="text-content-3 transition hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
